import { ReactNode, memo, useContext, useId, useMemo } from "react";

import { GetLanguageValueAsString } from "@/app/application/Translate";
import {
	getSurfaceBorderColor,
	getSurfaceBorderStyle,
} from "@/app/application/theme";
import { TranslationContext } from "@/app/providers";
import { stringAvatar } from "@/features/procedures/lib/checklist";
import {
	ProcedureNodeStepComment,
	SuggestionResponse,
} from "@/features/procedures/model/procedure/ProcedureNodeStepComment";
import { feedbackCharCount } from "@/shared/ui/comment-suggestion/Variables";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Avatar,
	Box,
	Card,
	CardContent,
	Chip,
	IconButton,
	Link,
	Stack,
	Tooltip,
	Typography,
	alpha,
} from "@mui/material";
import { Language } from "@/model/Language/Language";

type AccentColor = "info" | "success" | "default";
type Attachment = { Url: string };

type Props = {
	title: ReactNode;
	commentId?: string;
	comment?: ProcedureNodeStepComment;
	suggestion?: SuggestionResponse;
	children: ReactNode;
	isExpanded: boolean;
	toggleExpand: () => void;
	isDeletable?: boolean;
	onDelete?: () => void;
	chip?: ReactNode;
	lineClamp?: number;
	/** Subtle left accent bar: "info" (comment), "success" (improvement), "default" (none) */
	accent?: AccentColor;
};

const clampStyles = (lines: number) => ({
	display: "-webkit-box",
	WebkitLineClamp: lines,
	WebkitBoxOrient: "vertical" as const,
	overflow: "hidden",
});

const toAttachments = (value: unknown): Attachment[] => {
	if (!Array.isArray(value)) return [];
	return value
		.map((item) => {
			if (!item || typeof item !== "object") return null;
			const url = (item as { Url?: unknown }).Url;
			if (typeof url !== "string") return null;
			return { Url: url };
		})
		.filter((item): item is Attachment => item !== null);
};

export const FeedbackCard = memo((props: Props) => {
	const translation: Language | null = useContext(TranslationContext);
	const {
		comment,
		suggestion,
		children,
		title,
		isExpanded,
		toggleExpand,
		isDeletable,
		onDelete,
		chip,
		lineClamp = 3,
		accent = "default",
	} = props;

	const headerId = useId();

	const isComment = Boolean(comment);
	const isSuggestion = Boolean(suggestion);

	const contentLength = useMemo(() => {
		if (isComment) return comment?.Value?.length ?? 0;
		if (isSuggestion) return suggestion?.Description?.length ?? 0;
		return 0;
	}, [isComment, isSuggestion, comment?.Value, suggestion?.Description]);

	const attachments = useMemo(() => {
		const commentAttachments = toAttachments(
			(comment as unknown as { Attachments?: unknown })?.Attachments,
		);
		if (commentAttachments.length > 0) return commentAttachments;
		return toAttachments(
			(suggestion as unknown as { Attachments?: unknown })?.Attachments,
		);
	}, [comment, suggestion]);

	const hasAttachments = attachments.length > 0;
	const shouldShowExpand = contentLength > feedbackCharCount || hasAttachments;

	const user = useMemo(
		() =>
			comment?.UserName ??
			(suggestion?.Anonymous
				? "Anonymous"
				: (suggestion?.CreatedBy?.Name ?? "—")),
		[comment?.UserName, suggestion?.Anonymous, suggestion?.CreatedBy?.Name],
	);

	const fileDisplay = (url: string) => {
		try {
			const u = new URL(
				url,
				typeof window === "undefined"
					? "http://localhost"
					: window.location.origin,
			);
			const raw = u.pathname.split("/").pop() || url;
			return decodeURIComponent(raw);
		} catch {
			return url;
		}
	};

	return (
		<Box sx={{ position: "relative", my: 0.75 }}>
			<Card
				variant="outlined"
				sx={(theme) => {
					const isDark = theme.palette.mode === "dark";
					const accentColor =
						accent === "info"
							? theme.palette.info.main
							: accent === "success"
								? theme.palette.success.main
								: "transparent";
					return {
						width: "100%",
						paddingBottom: 2,
						borderRadius: 2,
						overflow: "hidden",
						boxShadow: "none",
						border: "1px solid",
						borderColor: getSurfaceBorderColor(theme, "subtle"),
						transition: "border-color .2s ease, box-shadow .2s ease",
						"&:hover": {
							borderColor: theme.palette.divider,
							boxShadow: isDark
								? "0 2px 12px rgba(0,0,0,0.15)"
								: "0 2px 12px rgba(0,0,0,0.05)",
						},
						...(accent !== "default" && {
							borderLeft: "3px solid",
							borderLeftColor: alpha(accentColor, 0.85),
						}),
					};
				}}
				aria-labelledby={headerId}
			>
				<CardContent sx={{ py: 1.25, px: 1.75, "&:last-child": { pb: 1.25 } }}>
					{/* Header: compact row */}
					<Stack
						direction="row"
						alignItems="center"
						spacing={1}
						sx={{ mb: 1, minHeight: 28 }}
						flexWrap="wrap"
						useFlexGap
					>
						<Tooltip title={user}>
							<Avatar
								{...stringAvatar(user)}
								alt={user}
								sx={{
									width: 28,
									height: 28,
									fontSize: "0.75rem",
									border: (t) => getSurfaceBorderStyle(t, "subtle"),
								}}
							/>
						</Tooltip>
						<Typography
							id={headerId}
							variant="subtitle2"
							sx={{
								fontWeight: 600,
								fontSize: "0.8125rem",
								lineHeight: 1.3,
								color: "text.primary",
							}}
						>
							{title}
						</Typography>
						{chip && (
							<Stack
								direction="row"
								spacing={0.75}
								alignItems="center"
								flexWrap="wrap"
								useFlexGap
							>
								{typeof chip === "string" ? (
									<Chip
										size="small"
										variant="outlined"
										label={chip}
										sx={{ borderRadius: 1.5, height: 24 }}
									/>
								) : (
									chip
								)}
							</Stack>
						)}
						<Box sx={{ flex: 1, minWidth: 8 }} />
						<Stack direction="row" spacing={0.25} alignItems="center">
							{isDeletable && (
								<Tooltip
									title={GetLanguageValueAsString(
										translation,
										"Common.Delete",
										"Delete",
									)}
								>
									<IconButton
										size="small"
										onClick={onDelete}
										aria-label={GetLanguageValueAsString(
											translation,
											"Common.Delete",
											"Delete",
										)}
										sx={{ p: 0.5 }}
									>
										<CloseIcon sx={{ fontSize: 18 }} />
									</IconButton>
								</Tooltip>
							)}
							{shouldShowExpand && (
								<Tooltip
									title={
										isExpanded
											? GetLanguageValueAsString(
													translation,
													"Common.ShowLess",
													"Show less",
												)
											: GetLanguageValueAsString(
													translation,
													"Common.ShowMore",
													"Show more",
												)
									}
								>
									<IconButton
										size="small"
										onClick={toggleExpand}
										aria-label={
											isExpanded
												? GetLanguageValueAsString(
														translation,
														"Common.Collapse",
														"Collapse",
													)
												: GetLanguageValueAsString(
														translation,
														"Common.Expand",
														"Expand",
													)
										}
										aria-expanded={isExpanded}
										sx={{ p: 0.5 }}
									>
										{isExpanded ? (
											<ExpandLessIcon sx={{ fontSize: 18 }} />
										) : (
											<ExpandMoreIcon sx={{ fontSize: 18 }} />
										)}
									</IconButton>
								</Tooltip>
							)}
						</Stack>
					</Stack>

					{/* Body */}
					<Box sx={isExpanded ? undefined : clampStyles(lineClamp)}>
						{children}
					</Box>

					{/* Attachments: compact chips */}
					{hasAttachments && (
						<Stack
							direction="row"
							spacing={0.5}
							useFlexGap
							flexWrap="wrap"
							sx={{ mt: 1.25, pt: 1.25, borderTop: 1, borderColor: "divider" }}
						>
							{attachments.map((att, idx) => (
								<Chip
									key={idx}
									size="small"
									variant="outlined"
									icon={<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />}
									label={
										<Typography
											component="span"
											noWrap
											sx={{ maxWidth: 180 }}
											variant="caption"
										>
											{fileDisplay(att.Url)}
										</Typography>
									}
									component={Link}
									href={att.Url}
									target="_blank"
									rel="noopener noreferrer"
									clickable
									sx={{
										borderRadius: 1.5,
										height: 26,
										"& .MuiChip-icon": { fontSize: 16 },
									}}
								/>
							))}
						</Stack>
					)}
				</CardContent>
			</Card>
		</Box>
	);
});

FeedbackCard.displayName = "FeedbackCard";
